/**
 * Collection sub-category strip -> image-swatch carousel.
 *
 * DYNAMIC: the swatches are built from the sub-category links the current
 * collection page itself renders in its filter strip (label + destination),
 * so each collection (Dog, Cat, ...) shows its own sub-categories. Pictures
 * come from a curated map for known handles, else the collection's own image
 * (or its first product's image) via the Storefront API.
 *
 * Same slot as the original strip: below the H1 banner, above "Filter and sort".
 *
 * Targets:
 *   div.link-filter__wrapper--mobile  (mobile strip — primary)
 *   div.link-filter__wrapper          (desktop sidebar twin — same treatment)
 *
 * Design tokens taken from the merchant's theme source:
 *   font: DM Sans (inherited), purple #473798 (filter nav link colour),
 *   orange #ffa400 (theme button/accent), 20px tile corners.
 */
(function () {
  "use strict";

  // --- Experiment identifiers (resolved from the runtime when available) ------
  var CTX =
    (window.Variant && (window.Variant.context || window.Variant.experiment)) ||
    {};
  var EXPERIMENT_ID = CTX.experiment_id || "";
  var VARIATION_ID = CTX.variation_id || "";

  var STYLE_ID = "v-subcat-swatches-style";
  var APPLIED_ATTR = "data-v-subcat-swatches";
  var MOBILE_SELECTOR = "div.link-filter__wrapper--mobile";
  var DESKTOP_SELECTOR = "div.link-filter__wrapper";

  // Storefront API — used to resolve a picture for each sub-category link that
  // the current collection page renders (collection image, else first product).
  var SF_ENDPOINT = "/api/2025-01/graphql.json";
  var SF_TOKEN = "b917a9638b574d938b23b81780f3af81";

  // Curated pictures for handles we have hand-picked art for. Any handle not in
  // this map falls back to the collection's own image from the Storefront API.
  var CURATED_IMAGES = {
    "adult-dog-food":
      "https://cdn.shopify.com/s/files/1/0821/8938/6020/files/100134_chicken_500g_1.png?v=1733858546",
    "treats-bones":
      "https://cdn.shopify.com/s/files/1/0821/8938/6020/files/lamb_puffs_1liver_1_1.png?v=1730743384",
    "all-supplements-dogs":
      "https://cdn.shopify.com/s/files/1/0821/8938/6020/files/fish_bone_broth_new_deisng.png?v=1733859222",
    "care-accessories-dogs":
      "https://cdn.shopify.com/s/files/1/0821/8938/6020/files/fish_lick_mat.jpg?v=1730743666",
  };

  // Cache resolved images per collection handle so the mobile + desktop strips
  // don't each fire their own network request for the same handle.
  var imageCache = {};

  // --- Small safe wrappers around the Variant APIs ---------------------------
  function reportFailure(reason, selector, description, errorMessage) {
    try {
      if (
        window.Variant &&
        typeof Variant.reportExperienceFailure === "function"
      ) {
        Variant.reportExperienceFailure(
          reason,
          selector,
          EXPERIMENT_ID,
          VARIATION_ID,
          description,
          errorMessage,
        );
      }
    } catch (e) {
      /* never let monitoring break the page */
    }
  }

  function track(name, extra) {
    try {
      if (window.Variant && typeof Variant.publishCustomEvent === "function") {
        var payload = {
          experiment_id: EXPERIMENT_ID,
          variation_id: VARIATION_ID,
        };
        if (extra) {
          for (var k in extra) {
            if (Object.prototype.hasOwnProperty.call(extra, k))
              payload[k] = extra[k];
          }
        }
        Variant.publishCustomEvent(name, payload);
      }
    } catch (e) {
      /* ignore */
    }
  }

  // --- Styles (theme tokens: DM Sans, purple links, orange accent) -----------
  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var css = [
      ".v-swatch-nav{width:100%;}",
      // Top padding gives the lifted (hover) tile room to rise without being
      // clipped behind the banner above. overflow-y:visible stops the
      // horizontal scroller from cutting off that upward movement.
      ".v-swatch-list{list-style:none;margin:0;padding:8px 0 6px 0;display:flex;align-items:flex-start;",
      "gap:8px;overflow-x:auto;overflow-y:visible;-webkit-overflow-scrolling:touch;scrollbar-width:none;",
      "scroll-snap-type:x proximity;}",
      ".v-swatch-list::-webkit-scrollbar{display:none;}",
      ".v-swatch-item{flex:0 0 auto;scroll-snap-align:start;}",
      // Label colour comes from the section colour scheme (white on the dark
      // collection background); the desktop card override is below.
      ".v-swatch-link{display:flex;flex-direction:column;align-items:center;gap:8px;width:96px;",
      "text-decoration:none;font-family:inherit;color:rgb(var(--color-foreground,255 255 255));}",
      // Rounded-square tiles that echo the product cards below.
      ".v-swatch-tile{display:flex;align-items:center;justify-content:center;width:92px;height:92px;",
      "border-radius:20px;background:#ffffff;",
      "border:1px solid rgba(69,65,66,.10);overflow:hidden;",
      "box-sizing:border-box;transition:transform .2s ease,border-color .2s ease,box-shadow .2s ease;}",
      ".v-swatch-tile img{width:100%;height:100%;object-fit:contain;padding:4px;box-sizing:border-box;",
      "display:block;}",
      // Fallback when a collection has no picture at all — keep a clean tile.
      ".v-swatch-tile--noimg{background:rgba(180,180,222,.35);}",
      ".v-swatch-label{font-size:1.3rem;line-height:1.25;font-weight:700;text-align:center;",
      "letter-spacing:.01em;color:inherit;}",
      ".v-swatch-link:hover .v-swatch-tile,.v-swatch-link:focus-visible .v-swatch-tile{",
      "transform:translateY(-2px);border-color:#ffa400;box-shadow:0 2px 8px rgba(69,65,66,.14);}",
      ".v-swatch-link:focus-visible{outline:2px solid #ffa400;outline-offset:3px;",
      "border-radius:20px;}",
      // Selected category: a bold brand-orange ring plus a soft outer halo so
      // the active swatch clearly stands out from the rest of the row.
      ".v-swatch-item.is-active .v-swatch-tile{border:3px solid #ffa400;",
      "box-shadow:0 0 0 3px rgba(255,164,0,.28);}",
      ".v-swatch-item.is-active .v-swatch-label{color:#ffa400;}",
      // More than 4 sub-categories: size the items so 3.5 fit across the row,
      // leaving the 4th visibly cut in half. That half-tile is the cue that the
      // row scrolls, instead of it ending flush on the edge and looking complete.
      ".v-swatch-list--peek .v-swatch-item{width:calc((100% - 24px) / 3.5);min-width:74px;}",
      ".v-swatch-list--peek .v-swatch-link{width:100%;}",
      ".v-swatch-list--peek .v-swatch-tile{width:100%;height:auto;aspect-ratio:1 / 1;}",
      // Desktop twin sits in the theme's light-purple sidebar card, so it keeps
      // the filter nav's purple link colour and wraps instead of scrolling.
      "@media screen and (min-width:750px){",
      ".link-filter__wrapper .v-swatch-list{flex-wrap:wrap;overflow-x:visible;gap:12px 8px;",
      "justify-content:center;}",
      // The desktop card wraps rather than scrolls, so no half-tile there.
      ".link-filter__wrapper .v-swatch-list--peek .v-swatch-item{width:96px;max-width:none;}",
      ".link-filter__wrapper .v-swatch-list--peek .v-swatch-tile{width:92px;height:92px;",
      "aspect-ratio:auto;}",
      ".link-filter__wrapper .v-swatch-link{color:#473798;}",
      ".link-filter__wrapper .v-swatch-item.is-active .v-swatch-label{color:#ffa400;}",
      "}",
    ].join("");
    var style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  }

  // --- Read the sub-category links the current collection page renders -------
  // This is what makes the row dynamic: instead of a fixed list, we take the
  // exact links (label + destination) the theme already output in the strip.
  function readOriginalItems(wrapper) {
    var items = [];
    var seen = {};
    if (!wrapper) return items;
    var links = wrapper.querySelectorAll("a.link-filter__link");
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      // Use the raw href attribute (not the proxy-rewritten .href property) so
      // handle extraction and navigation stay correct in preview and live.
      var href = a.getAttribute("href") || "";
      var label = (a.textContent || "").replace(/\s+/g, " ").trim();
      if (!href || !label) continue;
      if (href.indexOf("/collections") === -1) continue;
      if (seen[href]) continue;
      seen[href] = true;
      items.push({ label: label, url: href, handle: handleFromUrl(href) });
    }
    return items;
  }

  // Pull the collection handle out of a /collections/<handle> URL.
  function handleFromUrl(url) {
    var m = (url || "").match(/\/collections\/([^/?#]+)/);
    return m ? m[1] : "";
  }

  // Resolve a picture for one handle: curated art first, else the collection's
  // own image, else its first product's image. Cached per handle.
  function resolveImage(handle) {
    if (!handle) return Promise.resolve("");
    if (CURATED_IMAGES[handle]) return Promise.resolve(CURATED_IMAGES[handle]);
    if (imageCache[handle]) return imageCache[handle];

    var query =
      '{ collection(handle: "' +
      handle +
      '") { image { url } products(first: 5, sortKey: BEST_SELLING) { edges { node { featuredImage { url } } } } } }';

    var p = fetch(SF_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SF_TOKEN,
      },
      body: JSON.stringify({ query: query }),
    })
      .then(function (res) {
        if (!res.ok) {
          res
            .text()
            .then(function (body) {
              reportFailure(
                "api_error",
                null,
                "fetch the collection picture for a sub-category swatch (handle " +
                  handle +
                  ")",
                JSON.stringify({
                  route: SF_ENDPOINT,
                  method: "POST",
                  handle: handle,
                  status: res.status,
                  body: (body || "").slice(0, 300),
                }),
              );
            })
            .catch(function () {});
          return "";
        }
        return res.json().then(function (json) {
          var col = json && json.data && json.data.collection;
          if (!col) return "";
          // Prefer a real PRODUCT photo (best-seller first) — it reads as a
          // category swatch far better than the collection's banner/lifestyle
          // image, which is what looked "weird" before. Fall back to the
          // collection image only if no product has a picture.
          var edges = (col.products && col.products.edges) || [];
          for (var i = 0; i < edges.length; i++) {
            var fi = edges[i] && edges[i].node && edges[i].node.featuredImage;
            if (fi && fi.url) return fi.url;
          }
          if (col.image && col.image.url) return col.image.url;
          return "";
        });
      })
      .catch(function (e) {
        reportFailure(
          "api_error",
          null,
          "fetch the collection picture for a sub-category swatch (handle " +
            handle +
            ")",
          JSON.stringify({
            route: SF_ENDPOINT,
            handle: handle,
            error: e && e.message ? e.message : String(e),
          }),
        );
        return "";
      });

    imageCache[handle] = p;
    return p;
  }

  // --- Build the carousel markup from the read items ------------------------
  // Returns a Promise that resolves once every swatch picture is set.
  function buildNav(items) {
    var nav = document.createElement("nav");
    nav.className = "v-swatch-nav";
    nav.setAttribute("aria-label", "Sub-categories");

    var list = document.createElement("ul");
    list.className = "v-swatch-list";
    // With more than 4 sub-categories the row scrolls, so shrink the items to
    // show 3.5 across — the half-visible 4th tile signals "swipe for more".
    if (items.length > 4) list.className += " v-swatch-list--peek";

    var path = window.location.pathname || "";
    var imagePromises = [];

    items.forEach(function (item) {
      var li = document.createElement("li");
      li.className = "v-swatch-item";
      if (item.handle && path.indexOf("/collections/" + item.handle) !== -1) {
        li.className += " is-active";
      }

      var a = document.createElement("a");
      a.className = "v-swatch-link";
      a.href = item.url;

      var tile = document.createElement("span");
      tile.className = "v-swatch-tile";

      var img = document.createElement("img");
      img.alt = item.label;
      img.setAttribute("width", "92");
      img.setAttribute("height", "92");
      img.setAttribute("loading", "eager");
      img.setAttribute("decoding", "async");
      tile.appendChild(img);

      // Resolve this swatch's picture (curated / collection / product image).
      imagePromises.push(
        resolveImage(item.handle).then(function (url) {
          if (url) {
            img.src = url;
          } else {
            // No picture available — keep the tile but hide the empty image.
            img.style.display = "none";
            tile.classList.add("v-swatch-tile--noimg");
          }
        }),
      );

      var label = document.createElement("span");
      label.className = "v-swatch-label";
      label.textContent = item.label;

      a.appendChild(tile);
      a.appendChild(label);

      // Click tracking with a redirect-safe pattern (the anchor navigates).
      a.addEventListener("click", function (e) {
        var destination = a.href;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) {
          track("influenced-revenue", {
            element: "subcategory_swatch",
            label: item.label,
            destination: item.url,
          });
          return;
        }
        e.preventDefault();
        var safety = setTimeout(function () {
          window.location.href = destination;
        }, 2000);
        track("influenced-revenue", {
          element: "subcategory_swatch",
          label: item.label,
          destination: item.url,
        });
        clearTimeout(safety);
        window.location.href = destination;
      });

      li.appendChild(a);
      list.appendChild(li);
    });

    // Fire once when the shopper actually swipes/scrolls the row.
    var scrolled = false;
    list.addEventListener(
      "scroll",
      function () {
        if (scrolled) return;
        scrolled = true;
        track("carousel_scrolled", { element: "subcategory_swatch_row" });
      },
      { passive: true },
    );

    nav.appendChild(list);
    return { nav: nav, ready: Promise.all(imagePromises) };
  }

  // --- Preload the swatch images so the row never pops in half-rendered ------
  function whenImagesReady(nav, done) {
    var imgs = nav.querySelectorAll("img");
    var pending = imgs.length;
    var finished = false;

    function finish() {
      if (finished) return;
      finished = true;
      done();
    }
    if (!pending) return finish();

    function one() {
      pending -= 1;
      if (pending <= 0) finish();
    }
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.complete && img.naturalWidth > 0) {
        one();
      } else if (img.decode) {
        img.decode().then(one, one);
      } else {
        img.addEventListener("load", one, { once: true });
        img.addEventListener("error", one, { once: true });
      }
    }
    // Safety net — never keep the strip hidden because of a slow/blocked image.
    setTimeout(finish, 2500);
  }

  // --- Replace one strip's contents -----------------------------------------
  function applyTo(wrapper, selector) {
    if (!wrapper || wrapper.getAttribute(APPLIED_ATTR) === "1") return false;

    // Read the links THIS collection page rendered before we touch anything.
    var items = readOriginalItems(wrapper);
    if (!items.length) {
      // No sub-category links on this page — leave the strip untouched rather
      // than blanking it, and report so we can see it if it's unexpected.
      wrapper.setAttribute(APPLIED_ATTR, "1");
      reportFailure(
        "element_not_found",
        selector + " a.link-filter__link",
        "build image swatches from the collection page's own sub-category links — no sub-category links were present in the strip to convert",
      );
      return false;
    }

    wrapper.setAttribute(APPLIED_ATTR, "1");

    try {
      var previousVisibility = wrapper.style.visibility;
      // Hold the region hidden until pictures are set + painted, then reveal in
      // one step — the original text links are removed at the same moment, so
      // the old strip and the new row are never visible together.
      wrapper.style.visibility = "hidden";

      var built = buildNav(items);
      wrapper.innerHTML = "";
      wrapper.appendChild(built.nav);

      var revealed = false;
      var reveal = function () {
        if (revealed) return;
        revealed = true;
        wrapper.style.visibility = previousVisibility || "";
      };

      // Reveal once picture URLs are resolved AND the images have painted.
      built.ready
        .then(function () {
          whenImagesReady(built.nav, reveal);
        })
        .catch(reveal);

      // Safety net — never keep the strip hidden if the API/images stall.
      setTimeout(reveal, 4000);
      return true;
    } catch (err) {
      wrapper.style.visibility = "";
      reportFailure(
        "mutation_failed",
        selector,
        "replace the text-only sub-category link strip with an image swatch carousel built from the collection page's own sub-category links",
        err && err.message ? err.message : String(err),
      );
      return false;
    }
  }

  // --- Exposure detector (identical in variant_EXPC96BBCD3-V0.js) -------------------
  function startExposureDetector() {
    var anchor =
      document.querySelector(MOBILE_SELECTOR) ||
      document.querySelector(DESKTOP_SELECTOR);
    if (!anchor) {
      reportFailure(
        "element_not_found",
        MOBILE_SELECTOR,
        "exposure detector: watch the sub-category link strip below the collection title entering the viewport",
      );
      return;
    }
    try {
      if (typeof IntersectionObserver !== "function") {
        if (window.Variant && typeof Variant.reportExposure === "function") {
          Variant.reportExposure(
            EXPERIMENT_ID,
            VARIATION_ID,
            "immediate",
            "sub-category link strip present below the collection title",
          );
        }
        return;
      }
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            try {
              if (
                window.Variant &&
                typeof Variant.reportExposure === "function"
              ) {
                Variant.reportExposure(
                  EXPERIMENT_ID,
                  VARIATION_ID,
                  "viewport",
                  "sub-category link strip below the collection title scrolled into view",
                );
              }
            } catch (e) {
              /* ignore */
            }
            io.disconnect();
            return;
          }
        }
      });
      io.observe(anchor);
    } catch (e) {
      /* ignore */
    }
  }

  // --- Boot ------------------------------------------------------------------
  function start() {
    injectStyles();

    var appliedAny = false;

    function tryApply() {
      var mobile = document.querySelector(MOBILE_SELECTOR);
      var desktop = document.querySelector(DESKTOP_SELECTOR);
      if (mobile && applyTo(mobile, MOBILE_SELECTOR)) appliedAny = true;
      if (desktop && applyTo(desktop, DESKTOP_SELECTOR)) appliedAny = true;
      return !!(mobile || desktop);
    }

    var found = tryApply();

    if (!found) {
      // The strip can be re-rendered by the theme's filter/pagination logic,
      // so keep watching for it instead of giving up immediately.
      var root = document.querySelector("#MainContent") || document.body;
      if (root) {
        var obs = new MutationObserver(function () {
          if (tryApply()) {
            obs.disconnect();
            clearTimeout(giveUp);
          }
        });
        obs.observe(root, { childList: true, subtree: true });
        var giveUp = setTimeout(function () {
          obs.disconnect();
          if (!appliedAny) {
            reportFailure(
              "element_not_found",
              MOBILE_SELECTOR,
              "replace the text-only sub-category link strip with an image swatch carousel below the collection title — the strip was never found on the collection page",
            );
          }
        }, 8000);
      } else {
        reportFailure(
          "element_not_found",
          MOBILE_SELECTOR,
          "replace the text-only dog sub-category link strip with an image swatch carousel below the collection title — the strip was never found on the collection page",
        );
      }
    }

    startExposureDetector();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
